import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { mindpulseAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Activity, 
  Wind, 
  Eye, 
  Hand, 
  Brain,
  ChevronRight,
  X,
  Check,
  Minus,
  ThumbsUp,
  Clock,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Zap
} from 'lucide-react';
import { InteractiveExercise } from './InteractiveExercises';

const RISK_STATE_CONFIG = {
  stable: { color: 'text-green-400', bg: 'bg-green-500/20', label: 'Balanced' },
  drifting: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Shifting' },
  elevated: { color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Needs Care' },
  high: { color: 'text-red-400', bg: 'bg-red-500/20', label: 'Attention Needed' },
  critical: { color: 'text-red-500', bg: 'bg-red-600/20', label: 'Reach Out' }
};

const CATEGORY_ICONS = {
  breathing: Wind,
  grounding: Eye,
  body: Hand,
  cognitive: Brain,
  interactive: Zap
};

const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60 ? `${seconds % 60}s` : ''}`;
};

export const MindPulse = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [intervention, setIntervention] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [exerciseComplete, setExerciseComplete] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [activeTab, setActiveTab] = useState('guided'); // 'guided' or 'interactive'

  // Filter templates based on active tab
  const filteredTemplates = templates.filter(t => {
    const exerciseType = t.exercise_type || 'guided';
    if (activeTab === 'interactive') {
      return exerciseType === 'interactive';
    }
    return exerciseType !== 'interactive';
  });

  const guidedCount = templates.filter(t => (t.exercise_type || 'guided') !== 'interactive').length;
  const interactiveCount = templates.filter(t => t.exercise_type === 'interactive').length;

  const fetchMindPulseData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, interventionRes] = await Promise.all([
        api.get('/mindpulse/status'),
        api.get('/mindpulse/intervention/check')
      ]);
      setStatus(statusRes.data);
      setIntervention(interventionRes.data);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        console.error('Failed to fetch MindPulse data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAIAnalysis = useCallback(async () => {
    try {
      setLoadingAI(true);
      const response = await mindpulseAPI.getAIAnalysis();
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Failed to fetch AI analysis:', error);
    } finally {
      setLoadingAI(false);
    }
  }, []);

  const refreshAIAnalysis = async () => {
    try {
      setLoadingAI(true);
      const response = await mindpulseAPI.refreshAnalysis();
      if (response.data.success) {
        setAiAnalysis(response.data.analysis);
        toast.success('AI analysis refreshed');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to refresh analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/mindpulse/micro-recovery/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, []);

  useEffect(() => {
    fetchMindPulseData();
    fetchAIAnalysis();
  }, [fetchMindPulseData, fetchAIAnalysis]);

  const startExercise = async (template) => {
    setSelectedTemplate(template);
    setShowExerciseModal(true);
    setCurrentStep(0);
    setExerciseComplete(false);
    
    try {
      const response = await api.post('/mindpulse/micro-recovery/start', {
        template_id: template.id
      });
      setActiveSession(response.data);
    } catch (error) {
      toast.error('Failed to start exercise');
    }
  };

  const completeExercise = async (feedback) => {
    if (!activeSession) return;
    
    try {
      await api.post('/mindpulse/micro-recovery/complete', {
        session_id: activeSession.id,
        feedback
      });
      toast.success('Great job! Your session has been recorded.');
      setShowExerciseModal(false);
      setActiveSession(null);
      setSelectedTemplate(null);
      setExerciseComplete(false);
      fetchMindPulseData();
    } catch (error) {
      toast.error('Failed to save feedback');
    }
  };

  const handleOpenExercises = async () => {
    await fetchTemplates();
    setShowExerciseModal(true);
    setSelectedTemplate(null);
  };

  const dismissIntervention = () => {
    setIntervention({ ...intervention, show_intervention: false });
  };

  // Show subscription required message
  if (accessDenied) {
    return (
      <div className="feature-card" data-testid="mindpulse-locked">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl" style={{ background: 'rgba(255, 140, 0, 0.15)' }}>
            <Activity className="w-6 h-6" style={{ color: '#FF8C00' }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>MindPulse™</h3>
            <p className="text-xs text-sunrise-light">Gentle alerts and quick resets</p>
          </div>
        </div>
        <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
          <Sparkles className="w-8 h-8 mx-auto mb-2" style={{ color: '#FF8C00' }} />
          <p className="text-sm mb-3 text-sunrise-medium">
            Unlock proactive emotional wellness monitoring with a subscription
          </p>
          <a 
            href="/profile" 
            className="text-sm font-medium hover:underline"
            style={{ color: '#FF8C00' }}
          >
            View Plans →
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feature-card animate-pulse">
        <div className="h-6 w-32 rounded mb-4" style={{ background: 'rgba(255, 165, 0, 0.2)' }}></div>
        <div className="h-20 rounded" style={{ background: 'rgba(255, 165, 0, 0.2)' }}></div>
      </div>
    );
  }

  const riskConfig = RISK_STATE_CONFIG[status?.risk_state] || RISK_STATE_CONFIG.stable;

  return (
    <>
      <div className="feature-card overflow-hidden" data-testid="mindpulse-widget">
        {/* Header */}
        <div className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl" style={{ background: 'rgba(255, 140, 0, 0.15)' }}>
                <Activity className="w-6 h-6" style={{ color: '#FF8C00' }} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>MindPulse™</h3>
                <p className="text-xs text-sunrise-light">Gentle alerts and quick resets</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${riskConfig.bg} ${riskConfig.color}`}>
              {riskConfig.label}
            </div>
          </div>

          {/* Intervention Banner */}
          {intervention?.show_intervention && (
            <div className="rounded-xl p-4 mb-4" style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1))', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm mb-2 text-sunrise-black">{intervention.message}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleOpenExercises}
                      className="px-4 py-2 text-white text-sm rounded-lg transition-opacity btn-primary-cta"
                      data-testid="try-reset-button"
                    >
                      {intervention.recommended_action}
                    </button>
                    <button
                      onClick={dismissIntervention}
                      className="px-4 py-2 text-sm transition-colors text-sunrise-light"
                    >
                      Maybe later
                    </button>
                  </div>
                </div>
                <button onClick={dismissIntervention} className="text-sunrise-light">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl p-3" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-sunrise-light" />
                <span className="text-xs text-sunrise-light">Sessions this week</span>
              </div>
              <span className="text-2xl font-bold text-sunrise-black">{status?.sessions_completed_this_week || 0}</span>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-sunrise-light" />
                <span className="text-xs text-sunrise-light">Wellness Score</span>
              </div>
              <span className="text-2xl font-bold text-sunrise-black">{Math.max(0, 100 - (status?.risk_score || 0))}%</span>
            </div>
          </div>

          {/* AI Insights Toggle */}
          <button
            onClick={() => setShowAIInsights(!showAIInsights)}
            className="w-full mt-4 p-3 rounded-xl flex items-center justify-between transition-all"
            style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1))', border: '1px solid rgba(168, 85, 247, 0.2)' }}
            data-testid="ai-insights-toggle"
          >
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5" style={{ color: '#A855F7' }} />
              <span className="text-sm font-medium text-sunrise-black">AI Wellness Insights</span>
              {aiAnalysis?.analysis_type === 'ai_powered' && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#A855F7' }}>AI</span>
              )}
            </div>
            <ChevronRight className={`w-5 h-5 transition-transform text-sunrise-light ${showAIInsights ? 'rotate-90' : ''}`} />
          </button>

          {/* AI Insights Panel */}
          {showAIInsights && (
            <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.05)', border: '1px solid rgba(255, 165, 0, 0.2)' }} data-testid="ai-insights-panel">
              {loadingAI ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#A855F7' }} />
                  <span className="ml-2 text-sm text-sunrise-light">Analyzing your wellness patterns...</span>
                </div>
              ) : aiAnalysis ? (
                <div className="space-y-4">
                  {/* Personalized Insight */}
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#A855F7' }} />
                      <p className="text-sm text-sunrise-black">{aiAnalysis.personalized_insight}</p>
                    </div>
                  </div>

                  {/* Key Concerns */}
                  {aiAnalysis.key_concerns?.length > 0 && (
                    <div>
                      <p className="text-xs mb-2 flex items-center gap-1 text-sunrise-light">
                        <AlertTriangle className="w-3 h-3" /> Areas to Watch
                      </p>
                      <ul className="space-y-1">
                        {aiAnalysis.key_concerns.map((concern, idx) => (
                          <li key={idx} className="text-sm flex items-center gap-2" style={{ color: '#EAB308' }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#EAB308' }} />
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Positive Indicators */}
                  {aiAnalysis.positive_indicators?.length > 0 && (
                    <div>
                      <p className="text-xs mb-2 flex items-center gap-1 text-sunrise-light">
                        <CheckCircle className="w-3 h-3" /> Positive Signs
                      </p>
                      <ul className="space-y-1">
                        {aiAnalysis.positive_indicators.map((indicator, idx) => (
                          <li key={idx} className="text-sm text-green-400 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            {indicator}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommended Actions */}
                  {aiAnalysis.recommended_actions?.length > 0 && (
                    <div>
                      <p className="text-xs text-sunrise-light mb-2">Recommended Actions</p>
                      <ul className="space-y-2">
                        {aiAnalysis.recommended_actions.map((action, idx) => (
                          <li key={idx} className="text-sm text-sunrise-black flex items-start gap-2">
                            <span className="font-bold" style={{ color: '#FF8C00' }}>{idx + 1}.</span>
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Refresh Button */}
                  <button
                    onClick={refreshAIAnalysis}
                    disabled={loadingAI}
                    className="w-full mt-2 py-2 text-sm text-purple-400 hover:text-purple-300 flex items-center justify-center gap-2 transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingAI ? 'animate-spin' : ''}`} />
                    Refresh Analysis
                  </button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  AI analysis not available. Keep logging your moods to get personalized insights.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Quick Action */}
        <button
          onClick={handleOpenExercises}
          className="w-full p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-t border-border hover:from-cyan-500/20 hover:to-blue-500/20 transition-colors flex items-center justify-between"
          data-testid="open-exercises-button"
        >
          <span className="text-sm font-medium text-foreground">Start a Micro-Recovery</span>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Exercise Modal */}
      {showExerciseModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="exercise-modal">
          <div className="bg-card rounded-2xl w-full max-w-md overflow-hidden">
            {!selectedTemplate ? (
              // Template Selection with Tabs
              <>
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-foreground">Micro-Recovery Moments</h2>
                    <button onClick={() => setShowExerciseModal(false)} className="text-muted-foreground hover:text-foreground">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  {/* Tabs */}
                  <div className="flex gap-2 p-1 bg-muted/50 rounded-xl" data-testid="exercise-tabs">
                    <button
                      onClick={() => setActiveTab('guided')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'guided'
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      data-testid="tab-guided"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Wind className="w-4 h-4" />
                        <span>Guided</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          activeTab === 'guided' ? 'bg-primary-foreground/20' : 'bg-muted'
                        }`}>
                          {guidedCount}
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('interactive')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'interactive'
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-md'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      data-testid="tab-interactive"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Zap className="w-4 h-4" />
                        <span>Interactive</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          activeTab === 'interactive' ? 'bg-white/20' : 'bg-muted'
                        }`}>
                          {interactiveCount}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Tab Description */}
                <div className="px-4 py-3 bg-muted/30 border-b border-border">
                  {activeTab === 'guided' ? (
                    <p className="text-xs text-muted-foreground flex items-center gap-2">
                      <Wind className="w-3 h-3" />
                      Step-by-step breathing, grounding, and relaxation exercises
                    </p>
                  ) : (
                    <p className="text-xs text-yellow-400 flex items-center gap-2">
                      <Zap className="w-3 h-3" />
                      Engaging activities to release tension and improve focus
                    </p>
                  )}
                </div>
                
                {/* Exercise List */}
                <div className="p-4 max-h-[50vh] overflow-y-auto space-y-3">
                  {filteredTemplates.length > 0 ? (
                    filteredTemplates.map(template => {
                      const IconComponent = template.exercise_type === 'interactive' ? Zap : (CATEGORY_ICONS[template.category] || Activity);
                      const isInteractive = template.exercise_type === 'interactive';
                      return (
                        <button
                          key={template.id}
                          onClick={() => startExercise(template)}
                          className={`w-full p-4 rounded-xl hover:bg-muted transition-colors text-left ${isInteractive ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' : 'bg-muted/50'}`}
                          data-testid={`template-${template.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${isInteractive ? 'bg-yellow-500/20' : 'bg-primary/20'}`}>
                              <IconComponent className={`w-5 h-5 ${isInteractive ? 'text-yellow-400' : 'text-primary'}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="font-medium text-foreground">{template.title}</h3>
                                <span className="text-xs text-muted-foreground">{formatDuration(template.duration_seconds)}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No exercises available</p>
                    </div>
                  )}
                </div>
              </>
            ) : !exerciseComplete ? (
              // Exercise Steps - Check if interactive or guided
              selectedTemplate.exercise_type === 'interactive' ? (
                // Interactive Exercise
                <>
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-yellow-400" />
                        <h2 className="text-lg font-bold text-foreground">{selectedTemplate.title}</h2>
                      </div>
                      <button onClick={() => { setShowExerciseModal(false); setSelectedTemplate(null); }} className="text-muted-foreground hover:text-foreground">
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  <InteractiveExercise 
                    template={selectedTemplate}
                    onComplete={() => setExerciseComplete(true)}
                    onExit={() => { setShowExerciseModal(false); setSelectedTemplate(null); }}
                  />
                </>
              ) : (
                // Guided Exercise Steps
                <>
                  <div className="p-6 border-b border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-foreground">{selectedTemplate.title}</h2>
                        <p className="text-sm text-muted-foreground">{formatDuration(selectedTemplate.duration_seconds)}</p>
                      </div>
                      <button onClick={() => { setShowExerciseModal(false); setSelectedTemplate(null); }} className="text-muted-foreground hover:text-foreground">
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    {/* Progress */}
                    <div className="flex gap-1 mb-6">
                      {selectedTemplate.steps.map((_, idx) => (
                        <div 
                          key={idx} 
                          className={`flex-1 h-1 rounded-full transition-colors ${idx <= currentStep ? 'bg-primary' : 'bg-muted'}`}
                        />
                      ))}
                    </div>

                    {/* Current Step */}
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                        <span className="text-2xl font-bold text-cyan-400">{currentStep + 1}</span>
                      </div>
                      <p className="text-lg text-foreground mb-8">{selectedTemplate.steps[currentStep]}</p>
                      
                      <div className="flex gap-3 justify-center">
                        {currentStep > 0 && (
                          <button
                            onClick={() => setCurrentStep(currentStep - 1)}
                            className="px-6 py-3 bg-muted text-foreground rounded-xl hover:bg-muted/70 transition-colors"
                          >
                            Back
                          </button>
                        )}
                        {currentStep < selectedTemplate.steps.length - 1 ? (
                          <button
                            onClick={() => setCurrentStep(currentStep + 1)}
                            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:opacity-90 transition-opacity"
                            data-testid="next-step-button"
                          >
                            Next
                          </button>
                        ) : (
                          <button
                            onClick={() => setExerciseComplete(true)}
                            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:opacity-90 transition-opacity"
                            data-testid="complete-exercise-button"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )
            ) : (
              // Feedback
              <>
                <div className="p-6 border-b border-border">
                  <h2 className="text-xl font-bold text-foreground text-center">How do you feel?</h2>
                </div>
                <div className="p-6">
                  <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-muted-foreground mb-6">Did this exercise help?</p>
                    
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => completeExercise('helpful')}
                        className="flex flex-col items-center gap-2 p-4 bg-green-500/10 rounded-xl hover:bg-green-500/20 transition-colors"
                        data-testid="feedback-helpful"
                      >
                        <ThumbsUp className="w-6 h-6 text-green-400" />
                        <span className="text-sm text-foreground">Yes</span>
                      </button>
                      <button
                        onClick={() => completeExercise('neutral')}
                        className="flex flex-col items-center gap-2 p-4 bg-yellow-500/10 rounded-xl hover:bg-yellow-500/20 transition-colors"
                        data-testid="feedback-neutral"
                      >
                        <Minus className="w-6 h-6 text-yellow-400" />
                        <span className="text-sm text-foreground">A little</span>
                      </button>
                      <button
                        onClick={() => completeExercise('not_helpful')}
                        className="flex flex-col items-center gap-2 p-4 bg-red-500/10 rounded-xl hover:bg-red-500/20 transition-colors"
                        data-testid="feedback-not-helpful"
                      >
                        <X className="w-6 h-6 text-red-400" />
                        <span className="text-sm text-foreground">Not really</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
